<!-- src/components/LoginScreen.vue -->
<template>
  <div
    class="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4"
  >
    <div class="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md">
      <!-- Logo section -->
      <div class="text-center mb-8">
        <img
          src="../assets/hotjet-logo.png"
          alt="HotJet Logo"
          class="h-16 mx-auto mb-4"
        />
        <h1 class="text-2xl font-bold text-gray-900 mb-2">Köhler Inteligent</h1>
        <p class="text-gray-600">Centrální mozek tepelných čerpadel Hotjet</p>
      </div>

      <!-- Auth section -->
      <div class="space-y-4">
        <!-- Environment message -->
        <div
          v-if="environmentMessage"
          :class="{
            'bg-yellow-50 border-yellow-200':
              environmentMessage.type === 'warning',
            'bg-blue-50 border-blue-200': environmentMessage.type === 'info',
          }"
          class="border rounded-lg p-3"
        >
          <p
            :class="{
              'text-yellow-700': environmentMessage.type === 'warning',
              'text-blue-700': environmentMessage.type === 'info',
            }"
            class="text-sm flex items-center"
          >
            <span class="mr-2">{{ environmentMessage.icon }}</span>
            {{ environmentMessage.message }}
          </p>
        </div>

        <!-- Error message s akcí -->
        <div
          v-if="errorState.type"
          class="bg-red-50 border border-red-200 rounded-lg p-3"
        >
          <div class="flex items-center justify-between">
            <p class="text-red-700 text-sm">
              {{ errorState.message }}
            </p>
            <button
              v-if="errorState.action"
              @click="errorState.action.action"
              class="text-sm text-red-600 hover:text-red-800 underline ml-2"
            >
              {{ errorState.action.label }}
            </button>
          </div>
        </div>

        <!-- Login button -->
        <button
          @click="handleGoogleLogin"
          :disabled="authState !== 'idle'"
          class="w-full flex items-center justify-center px-4 py-3 border border-gray-300 rounded-lg shadow-sm bg-white text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <svg
            v-if="authState === 'idle'"
            class="w-5 h-5 mr-3"
            viewBox="0 0 24 24"
          >
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          <div
            v-else
            class="w-5 h-5 mr-3 border-2 border-gray-300 border-t-indigo-600 rounded-full animate-spin"
          ></div>
          <span>{{ buttonText }}</span>
        </button>

        <!-- Dev Login (pouze v development) -->
        <button
          v-if="isDevMode"
          @click="handleDevLogin"
          :disabled="authState !== 'idle'"
          class="w-full flex items-center justify-center px-4 py-3 border-2 border-yellow-400 rounded-lg shadow-sm bg-yellow-50 text-yellow-800 hover:bg-yellow-100 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
        >
          <svg
            class="w-5 h-5 mr-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              stroke-linecap="round"
              stroke-linejoin="round"
              stroke-width="2"
              d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4"
            />
          </svg>
          <span>🔧 Dev Login (bypass)</span>
        </button>

        <!-- Info o přihlášení -->
        <div class="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <p class="text-blue-700 text-sm">
            <svg
              class="w-4 h-4 inline mr-1"
              fill="currentColor"
              viewBox="0 0 20 20"
            >
              <path
                fill-rule="evenodd"
                d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                clip-rule="evenodd"
              />
            </svg>
            Pro přístup do mozku je nutné přihlášení!
          </p>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

<script setup>
import { ref, onMounted, onUnmounted, computed } from "vue";

const emit = defineEmits(["login-success"]);

// Basic state
const isLoading = ref(false);
const errorMessage = ref("");
const isSafari = ref(false);
const isGuestMode = ref(false);
let authWindow = null;
let popupChecker = null;

// Enhanced states
const authState = ref("idle"); // idle, loading, authenticating, error
const errorState = ref({
  type: "", // google-unavailable, not-signed-in, popup-blocked, network, auth
  message: "",
  action: null,
});

// Computed properties pro UI states
const loadingMessage = computed(() => {
  switch (authState.value) {
    case "loading":
      return "Načítám Google služby...";
    case "authenticating":
      return "Zpracovávám přihlášení...";
    default:
      return "";
  }
});

const buttonText = computed(() => {
  if (authState.value !== "idle") {
    return loadingMessage.value;
  }
  if (isSafari.value) {
    return "Přihlásit se přes Google (Safari)";
  }
  if (isGuestMode.value) {
    return "Přihlásit se do Google účtu";
  }
  return "Přihlásit se přes Google";
});

// Environment-specific messages
const environmentMessage = computed(() => {
  if (isGuestMode.value) {
    return {
      type: "warning",
      icon: "👤",
      message: "Pro plnou funkcionalitu se přihlaste do Google účtu",
    };
  }
  if (isSafari.value) {
    return {
      type: "info",
      icon: "🔐",
      message: "Používá se bezpečné OAuth přihlášení přes Google",
    };
  }
  return null;
});

// Dev login jen v lokálním vite dev serveru. Do buildu (preview i produkce)
// se tlačítko nedostane.
const isDevMode = computed(() => import.meta.env.DEV);

// Inicializace
onMounted(() => {
  detectEnvironment();
  initializeAuth();

  // Listener pro localStorage fallback (Safari cross-window communication)
  window.addEventListener('storage', handleStorageAuthFallback);
});

// Cleanup
onUnmounted(() => {
  if (authWindow) {
    authWindow.close();
  }
  if (popupChecker) {
    clearInterval(popupChecker);
  }
  window.removeEventListener('storage', handleStorageAuthFallback);
});

// Google API availability check
async function isGoogleApiAvailable() {
  return new Promise((resolve) => {
    let attempts = 0;
    const maxAttempts = 50; // 5 sekund - více času pro Android

    const checkGoogle = () => {
      if (window.google?.accounts?.id) {
        resolve(true);
      } else if (attempts >= maxAttempts) {
        console.error("❌ Google API failed to load after", attempts * 100, "ms");
        resolve(false);
      } else {
        attempts++;
        setTimeout(checkGoogle, 100);
      }
    };

    checkGoogle();
  });
}

// Environment detection
function detectEnvironment() {
  // Safari detection
  isSafari.value = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);

  // Guest mode detection
  try {
    localStorage.setItem("test", "test");
    localStorage.removeItem("test");
    isGuestMode.value = false;
  } catch (e) {
    isGuestMode.value = true;
  }

}

// Enhanced initialization
async function initializeAuth() {
  authState.value = "loading";

  try {
    // Pokud je Safari nebo Google SDK nedostupný, použij Safari OAuth flow
    if (isSafari.value) {
      await setupSafariLogin();
      return;
    }

    // Kontrola Google API dostupnosti
    const googleAvailable = await isGoogleApiAvailable();

    if (!googleAvailable) {
      console.warn("⚠️ Google SDK not available - falling back to Safari OAuth flow");
      // Fallback na Safari OAuth flow i pro non-Safari browsery
      isSafari.value = true; // Force Safari mode
      await setupSafariLogin();
      return;
    }

    await initializeGoogleAuth();
    authState.value = "idle";
  } catch (error) {
    console.error("Auth initialization error:", error);
    // Poslední pokus - fallback na Safari OAuth
    isSafari.value = true;
    await setupSafariLogin();
  }
}

// Safari login setup
function setupSafariLogin() {
  authState.value = "idle";
}

// Initialize Google Auth
async function initializeGoogleAuth() {
  try {
    if (!window.google) {
      const script = document.createElement("script");
      script.src = "https://accounts.google.com/gsi/client";
      script.onload = setupGoogleAuth;
      document.head.appendChild(script);
    } else {
      setupGoogleAuth();
    }
  } catch (error) {
    console.error("Google init error:", error);
    handleError(error, "google-unavailable");
  }
}

// Enhanced Google setup
function setupGoogleAuth() {
  try {
    // Dynamická detekce domény pro Google OAuth
    const currentOrigin = window.location.origin;

    // Fallback mechanismus pro různé prostředí
    const googleConfig = {
      client_id: import.meta.env.VITE_GOOGLE_CLIENT_ID,
      callback: handleCredentialResponse,
      auto_select: false,
      cancel_on_tap_outside: false,
      use_fedcm_for_prompt: false,
      ux_mode: "popup",
      context: "signin",
    };

    // Přidat origin-specific konfiguraci
    if (currentOrigin.includes("localhost")) {
      googleConfig.origin = currentOrigin;
    }

    window.google.accounts.id.initialize(googleConfig);

    // Pre-render button pro fallback
    const buttonContainer = document.createElement("div");
    buttonContainer.id = "google-button-container";
    buttonContainer.style.cssText = "position:absolute;z-index:-1;opacity:0;";
    document.body.appendChild(buttonContainer);

    try {
      window.google.accounts.id.renderButton(buttonContainer, {
        type: "standard",
        theme: "outline",
        size: "large",
        text: "signin_with",
        shape: "rectangular",
        width: 250,
      });

      // Počkej na vykreslení tlačítka
      setTimeout(() => {
      }, 500);
    } catch (error) {
      console.error("Error rendering button:", error);
    }

    authState.value = "idle";
  } catch (error) {
    console.error("Google init error:", error);
    handleError(error, "google-unavailable");
  }
}

// Error handlers
function handleError(error, type) {
  console.error("Auth error:", error);

  errorState.value = {
    type,
    message: getErrorMessage(type),
    action: getErrorAction(type),
  };

  // Detailnější error message pro debugging
  if (import.meta.env.DEV) {
    console.debug("Auth error details:", {
      type,
      originalError: error,
      googleAvailable: !!window.google?.accounts?.id,
      cookiesEnabled: navigator.cookieEnabled,
      thirdPartyCookies: document.cookie.includes("_ga"),
      localStorage: (() => {
        try {
          localStorage.setItem("test", "test");
          localStorage.removeItem("test");
          return true;
        } catch (e) {
          return false;
        }
      })(),
    });
  }

  authState.value = "error";
  isLoading.value = false;
}

function getErrorMessage(type) {
  const isAndroid = /android/i.test(navigator.userAgent);

  switch (type) {
    case "google-unavailable":
      // Nevypisujeme chybu - automaticky fallbackujeme na Safari OAuth
      return "";
    case "not-signed-in":
      return "Pro pokračování se přihlaste do Google účtu";
    case "popup-blocked":
      return "Povolit vyskakovací okna pro přihlášení";
    case "network":
      return "Problém s připojením k internetu";
    case "auth":
      return "Chyba při ověřování přihlášení";
    default:
      return "Nastala neočekávaná chyba";
  }
}

function getErrorAction(type) {
  switch (type) {
    case "google-unavailable":
      // Žádná akce - už fallbackujeme automaticky
      return null;
    case "not-signed-in":
      return {
        label: "Přihlásit se do Google",
        action: () => window.open("https://accounts.google.com", "_blank"),
      };
    case "popup-blocked":
      return {
        label: "Jak povolit popups?",
        action: showPopupInstructions,
      };
    case "network":
      return {
        label: "Zkusit znovu",
        action: retryAuth,
      };
    default:
      return null;
  }
}

// Retry handlers
async function retryGoogleAuth() {
  errorState.value = { type: "", message: "", action: null };
  await initializeAuth();
}

async function retryAuth() {
  errorState.value = { type: "", message: "", action: null };
  await handleGoogleLogin();
}

function showPopupInstructions() {
  alert(`Jak povolit vyskakovací okna:
1. Klikněte na ikonu zámku v adresním řádku
2. Klikněte na 'Nastavení webu'
3. Povolte 'Vyskakovací okna a přesměrování'
4. Obnovte stránku`);
}

// Hlavní login handler
async function handleGoogleLogin() {
  isLoading.value = true;
  errorMessage.value = "";

  try {
    if (isSafari.value) {
      await handleSafariLogin();
    } else {
      await handleStandardLogin();
    }
  } catch (error) {
    console.error("Login error:", error);
    errorMessage.value =
      error.message || "Chyba při přihlašování. Zkuste to prosím znovu.";
    isLoading.value = false;
  }
}

// Standard login s retry logikou
async function handleStandardLogin() {
  try {
    // Nejdřív zkus prompt
    window.google.accounts.id.prompt((notification) => {

      if (notification.isNotDisplayed()) {
        triggerManualButton();
      } else if (notification.isSkippedMoment()) {
        triggerManualButton();
      }
    });
  } catch (error) {
    console.error("Standard login error:", error);
    triggerManualButton();
  }
}

// Helper pro manual button fallback
function triggerManualButton() {
  // Dej tlačítku čas na vykreslení
  setTimeout(() => {
    const container = document.getElementById("google-button-container");
    if (!container) {
      console.error("Button container not found");
      // Fallback na Safari OAuth flow
      isSafari.value = true;
      setupSafariLogin();
      return;
    }

    container.style.display = "block";
    const button = container.querySelector('div[role="button"]');
    if (button) {
      button.click();
      setTimeout(() => {
        container.style.display = "none";
      }, 100);
    } else {
      console.error("Google button element not found - falling back to Safari flow");
      container.style.display = "none";
      // Fallback na Safari OAuth flow místo zobrazení chyby
      isSafari.value = true;
      setupSafariLogin();
    }
  }, 300); // Počkej 300ms na vykreslení tlačítka
}

// Safari login implementace
async function handleSafariLogin() {
  try {
    const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    const redirectUri = `${window.location.origin}/api/auth/google-safari/callback`;

    // Generate cryptographic state nonce for CSRF protection
    const oauthState = crypto.randomUUID();
    sessionStorage.setItem('oauth_state', oauthState);

    // OAuth URL parametry
    const params = new URLSearchParams({
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: "openid email profile",
      access_type: "offline",
      prompt: "select_account",
      state: oauthState,
    });

    // Popup window parametry
    const width = 500;
    const height = 600;
    const left = window.screen.width / 2 - width / 2;
    const top = window.screen.height / 2 - height / 2;

    // Otevři popup
    authWindow = window.open(
      `https://accounts.google.com/o/oauth2/v2/auth?${params}`,
      "Google Login",
      `width=${width},height=${height},top=${top},left=${left},status=yes,toolbar=no,menubar=no`
    );

    if (!authWindow) {
      throw new Error(
        "Popup byl zablokován. Povolte prosím vyskakovací okna pro tento web."
      );
    }

    // Kontroluj zavření popupu (try/catch kvůli COOP policy na Google stránce)
    popupChecker = setInterval(() => {
      try {
        if (authWindow?.closed) {
          clearInterval(popupChecker);
          isLoading.value = false;
        }
      } catch {
        // COOP blokuje window.closed — popup se zavře přes message callback
      }
    }, 500);

    // Listener pro callback message
    window.addEventListener("message", handleSafariAuthSuccess, { once: true });
  } catch (error) {
    console.error("Google login error:", error);
    errorMessage.value = "Chyba při přihlašování. Zkuste to prosím znovu.";
    isLoading.value = false;
  }
}

// localStorage fallback handler (když window.opener selže)
async function handleStorageAuthFallback() {
  try {
    const authDataStr = localStorage.getItem('safari_auth_callback');
    if (!authDataStr) return;

    // Parse a validuj
    const authData = JSON.parse(authDataStr);
    const age = Date.now() - authData.timestamp;

    // Pouze pokud je fresh (< 10 sekund)
    if (age > 10000) {
      localStorage.removeItem('safari_auth_callback');
      return;
    }

    // Validate OAuth state to prevent CSRF
    const expectedState = sessionStorage.getItem('oauth_state');
    sessionStorage.removeItem('oauth_state');
    if (!expectedState || authData.state !== expectedState) {
      console.warn('OAuth state mismatch in localStorage fallback');
      localStorage.removeItem('safari_auth_callback');
      return;
    }

    // Vyčisti localStorage
    localStorage.removeItem('safari_auth_callback');

    // Zpracuj stejně jako window.postMessage
    await processAuthCode(authData.code);
  } catch (error) {
    console.error('Storage fallback error:', error);
  }
}

// Safari auth callback handler (window.postMessage)
async function handleSafariAuthSuccess(event) {
  if (event.origin !== window.location.origin) return;
  if (event.data.type !== 'GOOGLE_AUTH_SUCCESS') return;

  try {
    clearInterval(popupChecker);
    if (authWindow) {
      authWindow.close();
    }

    // Validate OAuth state to prevent CSRF
    const expectedState = sessionStorage.getItem('oauth_state');
    sessionStorage.removeItem('oauth_state');
    if (!expectedState || event.data.state !== expectedState) {
      throw new Error('OAuth state mismatch — possible CSRF attack');
    }

    const { code } = event.data;
    await processAuthCode(code);
  } catch (error) {
    console.error("Safari auth error:", error);
    errorMessage.value = error.message;
    isLoading.value = false;
  }
}

// Shared function pro zpracování auth code
async function processAuthCode(code) {
  try {

    // Pošli auth code na backend
    const response = await fetch("/api/auth/google-safari", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ code }),
    });

    const userData = await response.json();

    if (!response.ok) {
      throw new Error(userData.error);
    }

    // Ulož a emit
    localStorage.setItem(
      "userAuth",
      JSON.stringify({
        token: userData.token,
        user: userData.user,
        expiresAt: userData.expiresAt,
      })
    );

    emit("login-success", userData.user);
  } catch (error) {
    console.error("Auth code processing error:", error);
    errorMessage.value = error.message;
    throw error;
  } finally {
    isLoading.value = false;
  }
}

// Handle credential response
async function handleCredentialResponse(response) {
  try {
    // Odešli JWT token na backend pro ověření
    const loginResponse = await fetch("/api/auth/google", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        credential: response.credential,
      }),
    });

    if (!loginResponse.ok) {
      const errorData = await loginResponse.json().catch(() => ({}));
      const errorMsg = errorData.error || `Server error: ${loginResponse.status}`;
      console.error("Auth error response:", loginResponse.status, errorData);
      throw new Error(errorMsg);
    }

    const userData = await loginResponse.json();

    // Ulož údaje do localStorage s error handlingem
    try {
      localStorage.setItem(
        "userAuth",
        JSON.stringify({
          token: userData.token,
          user: userData.user,
          expiresAt: userData.expiresAt,
        })
      );
    } catch (storageError) {
      console.error("LocalStorage error (možná anonymní režim):", storageError);
      // Pokračuj i když localStorage selže - token je i v cookie
    }

    // Emit úspěšné přihlášení
    emit("login-success", userData.user);
  } catch (error) {
    console.error("Authentication error:", error);
    // Zobraz konkrétní chybovou zprávu od serveru
    errorMessage.value = error.message || "Chyba při přihlašování. Zkuste to prosím znovu.";
  } finally {
    isLoading.value = false;
  }
}

// Dev login bypass (pouze v development)
async function handleDevLogin() {
  try {
    // Secret se zadává ručně. Proměnná s prefixem VITE_ by se zabudovala
    // do klientského JavaScriptu a byla by veřejná.
    const secret = window.prompt("Dev admin secret (DEV_ADMIN_SECRET)");
    if (!secret) return;
    authState.value = "loading";

    const response = await fetch('/api/auth/dev-login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ secret, email: 'kohler@hotjet.cz' })
    });

    if (!response.ok) {
      throw new Error('Dev login failed');
    }

    const data = await response.json();

    // Ulož autentizaci
    try {
      localStorage.setItem(
        "userAuth",
        JSON.stringify({
          token: data.token,
          user: data.user,
          expiresAt: data.expiresAt,
        })
      );
    } catch (e) {
      console.warn("LocalStorage nedostupný:", e);
    }

    authState.value = "idle";
    emit("login-success", data.user);
  } catch (error) {
    console.error("Dev login error:", error);
    authState.value = "error";
    errorState.value = {
      type: "auth",
      message: "Dev login selhal - zkontroluj DEV_ADMIN_SECRET",
      action: null
    };
  }
}
</script>
